import { useQuery } from '@tanstack/react-query';

import { ASSET_TYPE_PLURALS, ASSET_TYPES } from '@/shared/api/openmetadata';
import { assetKeys } from '@/shared/lib/query/queryKeys';

import { fetchTableSampleData, fetchTopicSampleData } from '@/features/asset-detail/api';
import type { SampleDataResponse } from '@/features/asset-detail/api/sampleDataApi';

const SAMPLE_DATA_FETCH_HANDLERS: Record<string, (entityId: string) => Promise<SampleDataResponse | null>> = {
  [ASSET_TYPES.TABLE]: fetchTableSampleData,
  [ASSET_TYPE_PLURALS.TABLE]: fetchTableSampleData,
  [ASSET_TYPES.TOPIC]: fetchTopicSampleData,
  [ASSET_TYPE_PLURALS.TOPIC]: fetchTopicSampleData,
} as const;

export const useSampleData = (entityType: string, entityId: string, enabled: boolean = true) => {
  const query = useQuery({
    queryKey: assetKeys.sampleData(entityType, entityId),
    queryFn: async () => {
      const fetchHandler = SAMPLE_DATA_FETCH_HANDLERS[entityType];

      if (!fetchHandler) {
        console.warn(`Sample data not supported for entity type: ${entityType}`);
        return null;
      }

      return fetchHandler(entityId);
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!entityType && !!entityId,
    retry: 1,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
